import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { getDefaultHome } from '../utils/roleAccess';
import { findUserByEmail } from '../services/userService';
import { motion, AnimatePresence } from 'framer-motion';
import { FiInbox, FiLoader, FiMail, FiMoon, FiSend, FiSun, FiEye, FiEyeOff } from 'react-icons/fi';
import emailjs from '@emailjs/browser';

const EMAILJS_SERVICE  = process.env.REACT_APP_EMAILJS_SERVICE  || '';
const EMAILJS_TEMPLATE = process.env.REACT_APP_EMAILJS_TEMPLATE || '';
const EMAILJS_KEY      = process.env.REACT_APP_EMAILJS_KEY      || '';

const Logo = () => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: '10px' }}>
    <div style={{ width: '20px', height: '20px', backgroundColor: '#f25022' }} />
    <div style={{ width: '20px', height: '20px', backgroundColor: '#7fba00' }} />
    <div style={{ width: '20px', height: '20px', backgroundColor: '#00a4ef' }} />
    <div style={{ width: '20px', height: '20px', backgroundColor: '#ffb900' }} />
  </div>
);

const EyeIcon = ({ visible }) => (
  visible ? <FiEye size={18} /> : <FiEyeOff size={18} />
);

const Login = ({ setIsAuthenticated }) => {
  const loadLocalSetting = (key, defaultValue) => {
    try {
      const saved = localStorage.getItem(key);
      return saved !== null ? JSON.parse(saved) : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { t } = useI18n();
  const { setCurrentRole } = useAuth();

  const [toast, setToast] = useState({ visible: false, message: '', type: 'info' });
  const sceneRef = useRef(null);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    const updatePointerOffset = (event) => {
      const rect = scene.getBoundingClientRect();
      const x = ((event.clientX - (rect.left + rect.width / 2)) / rect.width) * 24;
      const y = ((event.clientY - (rect.top + rect.height / 2)) / rect.height) * 24;
      scene.style.setProperty('--pointer-x', `${x}px`);
      scene.style.setProperty('--pointer-y', `${y}px`);
    };

    const resetPointerOffset = () => {
      scene.style.setProperty('--pointer-x', '0px');
      scene.style.setProperty('--pointer-y', '0px');
    };

    scene.addEventListener('pointermove', updatePointerOffset);
    scene.addEventListener('pointerleave', resetPointerOffset);

    return () => {
      scene.removeEventListener('pointermove', updatePointerOffset);
      scene.removeEventListener('pointerleave', resetPointerOffset);
    };
  }, []);

  const showToast = (message, type = 'info', duration = 4000) => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((s) => ({ ...s, visible: false })), duration);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    const trimmed = forgotEmail.trim().toLowerCase();
    if (!trimmed) return;
    setForgotLoading(true);

    try {
      // Admin emailini tekshirish
      const adminEmail = (loadLocalSetting('smm_crm_email', process.env.REACT_APP_ADMIN_EMAIL || '')).toLowerCase();
      const adminPassword = loadLocalSetting('smm_crm_password', process.env.REACT_APP_ADMIN_PASSWORD || '');

      if (trimmed === adminEmail) {
        await emailjs.send(EMAILJS_SERVICE, EMAILJS_TEMPLATE, {
          to_email: adminEmail,
          to_name: 'Admin',
          login_email: adminEmail,
          login_password: adminPassword,
          login_role: 'Admin',
          site_url: window.location.origin,
        }, { publicKey: EMAILJS_KEY });
        setForgotDone(true);
        return;
      }

      // Firestore foydalanuvchilarini tekshirish
      const match = await findUserByEmail(trimmed);
      if (match) {
        await emailjs.send(EMAILJS_SERVICE, EMAILJS_TEMPLATE, {
          to_email: match.email,
          to_name: match.name || match.email,
          login_email: match.email,
          login_password: match.password,
          login_role: match.role || '',
          site_url: window.location.origin,
        }, { publicKey: EMAILJS_KEY });
        setForgotDone(true);
        return;
      }

      showToast("Bu email tizimda topilmadi!", 'error');
    } catch {
      showToast("Email yuborishda xato yuz berdi. Qayta urinib ko'ring.", 'error');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    const storedEmail = loadLocalSetting('smm_crm_email', process.env.REACT_APP_ADMIN_EMAIL || '').trim().toLowerCase();
    const storedPassword = loadLocalSetting('smm_crm_password', process.env.REACT_APP_ADMIN_PASSWORD || '');

    // Admin login (localStorage dan)
    if (normalizedEmail === storedEmail && password === storedPassword) {
      setCurrentRole('Admin', { id: 'admin', name: 'Admin', email: storedEmail, role: 'Admin' });
      localStorage.setItem('smm_crm_authenticated', 'true');
      setIsAuthenticated(true);
      navigate('/');
      return;
    }

    // Mutaxasis login — Firestore dan
    setLoginLoading(true);
    try {
      const match = await findUserByEmail(normalizedEmail);
      if (match && match.password === password) {
        const role = match.role || 'SMM Menejer';
        setCurrentRole(role, match);
        localStorage.setItem('smm_crm_authenticated', 'true');
        setIsAuthenticated(true);
        navigate(getDefaultHome(role));
        return;
      }
    } catch {
      // Firestore xatosi — foydalanuvchiga noto'g'ri ma'lumot xabari ko'rsatiladi
    } finally {
      setLoginLoading(false);
    }

    showToast(t('login.invalidCredentials') || "Email yoki parol noto'g'ri!", 'error');
  };

  const isLight = theme === 'light';

  return (
    <div ref={sceneRef} className={`login-scene ${isLight ? 'login-scene-light' : 'login-scene-dark'}`} style={{ 
      display: 'flex', justifyContent: 'center', alignItems: 'center', 
      height: '100vh', width: '100%',
      background: isLight 
        ? 'radial-gradient(circle at 50% 50%, #f1f5f9, #cbd5e1)' 
        : 'radial-gradient(circle at 50% 50%, #1e293b, #0f172a)',
      transition: 'background 0.5s ease',
      position: 'relative' // Pastdagi matn to'g'ri joylashishi uchun
    }}>
      <div className="login-liquid login-liquid-green" aria-hidden="true" />
      <div className="login-liquid login-liquid-green login-liquid-two" aria-hidden="true" />
      <div className="login-liquid login-liquid-blue" aria-hidden="true" />
      <div className="login-liquid login-liquid-blue login-liquid-two" aria-hidden="true" />
      <div className="login-liquid login-liquid-red" aria-hidden="true" />
      <div className="login-liquid login-liquid-red login-liquid-two" aria-hidden="true" />
      <div className="login-liquid login-liquid-yellow" aria-hidden="true" />
      <div className="login-liquid login-liquid-yellow login-liquid-two" aria-hidden="true" />
      
      <button 
        type="button"
        onClick={toggleTheme}
        aria-label="Theme toggle"
        className="theme-toggle-button"
        style={{
          position: 'absolute', top: '20px', right: '20px',
          width: '54px', height: '32px', borderRadius: '999px',
          border: isLight ? '1px solid rgba(15, 23, 42, 0.08)' : '1px solid rgba(255,255,255,0.16)',
          background: isLight ? 'rgba(255,255,255,0.8)' : 'rgba(15, 23, 42, 0.7)',
          boxShadow: isLight ? '0 8px 18px rgba(15, 23, 42, 0.12)' : '0 10px 24px rgba(2, 6, 23, 0.35)',
          cursor: 'pointer', backdropFilter: 'blur(14px)',
          display: 'flex', alignItems: 'center', padding: '4px',
          transition: 'all 0.3s ease', zIndex: 10
        }}
      >
        <span
          style={{
            width: '24px', height: '24px', borderRadius: '50%',
            background: isLight ? '#111827' : '#f8fafc',
            color: isLight ? '#f8fafc' : '#111827',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transform: isLight ? 'translateX(0)' : 'translateX(22px)',
            transition: 'all 0.25s ease',
            boxShadow: '0 4px 10px rgba(15, 23, 42, 0.20)'
          }}
        >
          {isLight ? <FiMoon size={13} /> : <FiSun size={13} />}
        </span>
      </button>

      <motion.form 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        onSubmit={handleLogin} 
        className="glass"
        style={{
          padding: 'clamp(24px, 5vw, 40px)', borderRadius: '24px',
          width: 'min(350px, calc(100vw - 32px))', display: 'flex', flexDirection: 'column',
          alignItems: 'center',
          color: isLight ? '#0f172a' : '#fff',
          transition: 'color 0.3s ease', position: 'relative', zIndex: 2
        }}
      >
        <Logo />
        <h1 style={{ fontSize: '24px', marginBottom: '5px' }}>{t('login.title')}</h1>
        <h2 style={{ fontSize: '16px', marginBottom: '20px', fontWeight: 'normal', opacity: 0.8 }}>{t('login.subtitle')}</h2>
          <>
            <input 
              type="email" placeholder={t('login.emailPlaceholder')} value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="login-input"
              style={{ 
                width: '100%', padding: '14px', margin: '8px 0', borderRadius: '12px', 
                border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
                background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.08)', 
                color: isLight ? '#0f172a' : '#fff', 
                outline: 'none'
              }}
            />
            
            <div style={{ position: 'relative', width: '100%' }}>
              <input 
                type={showPassword ? "text" : "password"}
                placeholder={t('login.passwordPlaceholder')} value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="login-input"
                style={{ 
                  width: '100%', padding: '14px', margin: '8px 0', borderRadius: '12px', 
                  border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
                  background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.08)', 
                  color: isLight ? '#0f172a' : '#fff', 
                  outline: 'none'
                }}
              />
              <button 
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{ 
                  position: 'absolute', right: '12px', top: '15px', 
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: isLight ? '#475569' : '#cbd5e1',
                  width: '32px', height: '32px', borderRadius: '8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.2s ease, color 0.2s ease'
                }}
              >
                <EyeIcon visible={showPassword} />
              </button>
            </div>

            <button type="submit" disabled={loginLoading} style={{
              width: '100%', padding: '14px', background: loginLoading ? '#64748b' : 'var(--accent-color)', color: '#fff',
              border: 'none', borderRadius: '12px', cursor: loginLoading ? 'not-allowed' : 'pointer', fontSize: '16px',
              fontWeight: 'bold', marginTop: '15px', transition: 'background 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
            }}>
              {loginLoading ? <><FiLoader size={15} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: '7px' }} /> Tekshirilmoqda...</> : t('login.submit')}
            </button>

            <button
              type="button"
              onClick={() => { setShowForgot(true); setForgotDone(false); setForgotEmail(""); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: isLight ? '#64748b' : '#94a3b8',
                fontSize: '13px', marginTop: '12px', textDecoration: 'underline',
                textDecorationStyle: 'dotted', padding: 0
              }}
            >
              Parolni unutdim?
            </button>
          </>
      </motion.form>

      {/* PAROLNI UNUTDIM MODALI */}
      <AnimatePresence>
        {showForgot && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowForgot(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', zIndex: 100 }}
            />
            <div style={{ position: 'fixed', inset: 0, zIndex: 101, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              className="glass"
              style={{
                pointerEvents: 'auto',
                width: 'min(360px, calc(100vw - 32px))',
                padding: 'clamp(24px, 5vw, 40px)',
                borderRadius: '24px',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                color: isLight ? '#0f172a' : '#fff',
              }}
            >
              <Logo />

              {!forgotDone ? (
                <form onSubmit={handleForgotSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <h2 style={{ fontSize: '20px', marginBottom: '6px', fontWeight: 'bold' }}>Parolni tiklash</h2>
                  <p style={{ fontSize: '13px', opacity: 0.6, textAlign: 'center', marginBottom: '20px', margin: '0 0 20px' }}>
                    Gmail manzilingizni kiriting — login va parol shu emailga yuboriladi.
                  </p>

                  <input
                    type="email"
                    autoFocus
                    placeholder="Gmail manzilingiz"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    required
                    style={{
                      width: '100%', padding: '14px', margin: '0 0 16px', borderRadius: '12px',
                      border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
                      background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.08)',
                      color: isLight ? '#0f172a' : '#fff',
                      outline: 'none', fontSize: '15px', boxSizing: 'border-box'
                    }}
                  />

                  <button
                    type="submit"
                    disabled={forgotLoading}
                    style={{
                      width: '100%', padding: '14px',
                      background: forgotLoading ? '#64748b' : 'var(--accent-color)',
                      color: '#fff', border: 'none', borderRadius: '12px',
                      cursor: forgotLoading ? 'not-allowed' : 'pointer',
                      fontSize: '15px', fontWeight: 'bold',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                    }}
                  >
                    {forgotLoading ? <><FiMail size={15} /> Yuborilmoqda...</> : <><FiSend size={15} /> Yuborish</>}
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowForgot(false)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: isLight ? '#64748b' : '#94a3b8',
                      fontSize: '13px', marginTop: '14px', padding: 0
                    }}
                  >
                    ← Loginga qaytish
                  </button>
                </form>
              ) : (
                <div style={{ width: '100%', textAlign: 'center' }}>
                  <FiInbox size={48} strokeWidth={1.5} style={{ marginBottom: '16px' }} aria-hidden="true" />
                  <h2 style={{ fontSize: '20px', marginBottom: '10px', fontWeight: 'bold' }}>Yuborildi!</h2>
                  <p style={{ fontSize: '13px', opacity: 0.7, marginBottom: '24px', lineHeight: 1.6 }}>
                    <strong>{forgotEmail}</strong> manziliga login va parol yuborildi.<br />
                    Pochtangizni tekshiring va kirishga harakat qiling.
                  </p>
                  <button
                    onClick={() => setShowForgot(false)}
                    style={{
                      width: '100%', padding: '14px',
                      background: 'var(--accent-color)', color: '#fff',
                      border: 'none', borderRadius: '12px',
                      cursor: 'pointer', fontSize: '15px', fontWeight: 'bold'
                    }}
                  >
                    Loginga qaytish
                  </button>
                </div>
              )}
            </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Ekran pastidagi xavfsizlik matni */}
      <p style={{
        position: 'absolute',
        bottom: '20px',
        fontSize: '12px',
        opacity: 0.6,
        color: isLight ? '#475569' : '#94a3b8',
        textAlign: 'center',
        width: '100%',
        pointerEvents: 'none',
        letterSpacing: '0.5px',
        transition: 'color 0.3s ease'
      }}>
        {t('login.footer')}
      </p>
      {/* Top-center toast */}
      {toast.visible && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 99999,
            minWidth: '260px',
            maxWidth: '90%',
            padding: '12px 18px',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(2,6,23,0.5)',
            color: '#fff',
            fontWeight: 700,
            textAlign: 'center',
            background: toast.type === 'error' ? 'linear-gradient(90deg,#ef4444,#dc2626)' : toast.type === 'success' ? 'linear-gradient(90deg,#16a34a,#059669)' : 'var(--accent-color)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {toast.type === 'error' ? (
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            ) : (
              <circle cx="12" cy="12" r="10" />
            )}
          </svg>
          <div style={{ flex: 1, fontSize: '14px' }}>{toast.message}</div>
        </div>
      )}
    </div>
  );
};

export default Login;